
import { defineComponent } from "vue";
import { IHopDongResponseBase } from "@/core/interfaces/ApiResponses";
import { HopDongService } from "@/core/services/HopDong.service";
import { swalErrNotification } from "@/core/helpers/utils";
import { formatAmount } from "@/core/helpers/number-format";
import { formatDisplayingDate } from "@/core/hooks/use-date-time-hook";
import { generateTablePdf } from "@/core/helpers/generate-table-pdf";
import PdfViewerPage from "@/components/shared/pdf-viewer/PdfViewerPage.vue";
import { PDF_GENERATE_TABLE_DEFAULTS } from "@/components/shared/pdf-viewer/pdf-content.config";
import { pickDonViPdfExtra } from "@/components/shared/pdf-viewer/pdf-document-header";

const HOP_DONG_PDF_COLUMNS = [
  { header: "STT", width: 18, align: "center" as const },
  { header: "Số HĐ", width: 40, align: "left" as const },
  { header: "Loại HĐ", width: 35, align: "left" as const },
  { header: "Đối tác", width: 70, align: "left" as const },
  { header: "Mã số thuế", width: 35, align: "left" as const },
  { header: "Giá trị (VNĐ)", width: 40, align: "right" as const },
  { header: "Ngày ký", width: 28, align: "center" as const },
  { header: "Ngày H/hạn", width: 28, align: "center" as const },
];

export default defineComponent({
  name: "HopDongListViewer",

  components: { PdfViewerPage },

  data() {
    return {
      items: [] as IHopDongResponseBase[],
      searchTerm: "",
      hopDongType: -1,
      pdfSource: null as string | null,
      isGenerating: false,
      tenDonVi: "",
      tenDonViCha: "",
    };
  },

  computed: {
    pdfFileName(): string {
      return "danh-sach-hop-dong.pdf";
    },

    subtitle(): string | undefined {
      const parts: string[] = [];
      if (this.searchTerm) parts.push(`Từ khóa: ${this.searchTerm}`);
      if (this.hopDongType === 1) parts.push("Loại: Mua bán");
      if (this.hopDongType === 2) parts.push("Loại: Điều chỉnh");
      return parts.length ? parts.join(" · ") : undefined;
    },
  },

  methods: {
    loaiHopDongLabel(item: IHopDongResponseBase): string {
      if (item.loai_hop_dong === 1) return "Mua bán";
      if (item.loai_hop_dong === 2) return "Điều chỉnh";
      return "-";
    },

    async loadAndGenerate() {
      const queryS = this.$route.query.s;
      this.searchTerm = typeof queryS === "string" ? queryS : "";

      const queryType = this.$route.query.loai;
      const parsedType =
        typeof queryType === "string" ? Number(queryType) : -1;
      this.hopDongType =
        Number.isFinite(parsedType) && parsedType > 0 ? parsedType : -1;

      try {
        this.isGenerating = true;
        this.pdfSource = null;

        const listResult = await HopDongService.listAll({
          searchTerm: this.searchTerm,
          hopDongType: this.hopDongType,
        });
        this.items = listResult.items;
        const { tenDonVi, tenDonViCha } = pickDonViPdfExtra(listResult.extra);
        this.tenDonVi = tenDonVi;
        this.tenDonViCha = tenDonViCha;
        this.pdfSource = await this.generatePdf();
      } catch (e) {
        swalErrNotification(e, "Có lỗi khi tải danh sách hợp đồng");
      } finally {
        this.isGenerating = false;
      }
    },

    async generatePdf(): Promise<string> {
      const rows = this.items.map((item, index) => [
        String(index + 1),
        item.so_hop_dong ?? "",
        this.loaiHopDongLabel(item),
        item.ten_doi_tac ?? "",
        item.ma_so_thue || "-",
        formatAmount(+item.total_value),
        formatDisplayingDate(item.ngay_ky),
        item.ngay_het_han ? formatDisplayingDate(item.ngay_het_han) : "-",
      ]);

      return generateTablePdf({
        ...PDF_GENERATE_TABLE_DEFAULTS,
        title: "DANH SÁCH HỢP ĐỒNG",
        subtitle: this.subtitle,
        columns: HOP_DONG_PDF_COLUMNS,
        rows,
        documentHeader: {
          tenDonVi: this.tenDonVi,
          tenDonViCha: this.tenDonViCha,
          variant: "masterList",
        },
      });
    },
  },

  mounted() {
    this.loadAndGenerate();
  },
});
